import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { fetchClasses } from "../../../../redux/admin/adminOperations";
import { Price, Class } from "../../../../redux/types/types";

interface EditPriceDialogProps {
  open: boolean;
  priceToEdit: Price | null;
  onClose: () => void;
  onSave: (price: Price) => void;
}

const EditPriceDialog: React.FC<EditPriceDialogProps> = ({
  open,
  priceToEdit,
  onClose,
  onSave,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { classes } = useSelector((state: RootState) => state.admin);

  const [formData, setFormData] = useState({
    priceType: "general", // "general" or "special"
    specialType: "",
    numberOfClasses: "",
    amount: "",
    currency: "chf",
    selectedClass: null as Class | null,
    isAdminOnly: false,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Fetch classes when dialog opens
  useEffect(() => {
    if (open) {
      dispatch(
        fetchClasses({
          page: 1,
          limit: 100,
          sortBy: "createdAt",
          sortOrder: "desc",
          search: "",
        })
      );
    }
  }, [open, dispatch]);

  const currencies = [{ value: "chf", label: "CHF (Fr)" }];

  useEffect(() => {
    if (priceToEdit) {
      let selectedClass = null;
      // Only set selectedClass for special prices with a valid classId
      if (
        priceToEdit.specialType &&
        priceToEdit.classId &&
        classes.length > 0
      ) {
        selectedClass =
          classes.find((cls) => cls.id === priceToEdit.classId) || null;
      }
      setFormData({
        priceType: priceToEdit.specialType ? "special" : "general",
        specialType: priceToEdit.specialType || "",
        numberOfClasses: priceToEdit.numberOfClasses.toString(),
        amount: (priceToEdit.amount / 100).toString(), // Convert from cents
        currency: priceToEdit.currency,
        selectedClass,
        isAdminOnly: priceToEdit.isAdminOnly || false,
      });
    }
  }, [priceToEdit, classes]);

  const handleInputChange = (field: string, value: string | boolean) => {
    // If enabling admin-only, set amount to 0 and clear any amount errors
    if (field === "isAdminOnly" && value === true) {
      setFormData((prev) => ({ ...prev, [field]: value, amount: "0" }));
      setErrors((prev) => ({ ...prev, amount: "" }));
    } else if (field === "isAdminOnly" && value === false) {
      // If disabling admin-only, clear the amount so user must enter a valid price
      setFormData((prev) => ({ ...prev, [field]: value, amount: "" }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    }
  };

  const handlePriceTypeChange = (value: string) => {
    const updates: any = { priceType: value };

    // Clear special-related fields when switching to general
    if (value === "general") {
      updates.specialType = "";
      updates.selectedClass = null;
    }

    setFormData((prev) => ({ ...prev, ...updates }));

    // Clear related errors
    if (value === "general") {
      setErrors((prev) => ({
        ...prev,
        specialType: "",
        selectedClass: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Validate special type if selected
    if (formData.priceType === "special") {
      if (!formData.specialType.trim()) {
        newErrors.specialType = intl.formatMessage({
          id: "admin.dashboard.specialTypeRequired",
        });
      }

      // For special prices, class selection is required
      if (!formData.selectedClass) {
        newErrors.selectedClass = intl.formatMessage({
          id: "admin.dashboard.classRequired",
        });
      } else if (!formData.selectedClass.isSpecial) {
        newErrors.selectedClass = intl.formatMessage({
          id: "admin.dashboard.onlySpecialClassesAllowed",
        });
      }
    }

    // Always validate numberOfClasses (required for all price types)
    if (!formData.numberOfClasses.trim()) {
      newErrors.numberOfClasses = intl.formatMessage({
        id: "admin.dashboard.numberOfClassesRequired",
      });
    } else {
      const numClasses = parseInt(formData.numberOfClasses);
      if (isNaN(numClasses) || numClasses <= 0) {
        newErrors.numberOfClasses = intl.formatMessage({
          id: "admin.dashboard.numberOfClassesInvalid",
        });
      }
    }

    if (!formData.amount.trim()) {
      newErrors.amount = intl.formatMessage({
        id: "admin.dashboard.amountRequired",
      });
    } else {
      const amount = parseFloat(formData.amount);
      // Allow 0 for admin-only prices
      if (isNaN(amount) || (!formData.isAdminOnly && amount <= 0)) {
        newErrors.amount = intl.formatMessage({
          id: "admin.dashboard.amountInvalid",
        });
      } else if (amount < 0) {
        newErrors.amount = intl.formatMessage({
          id: "admin.dashboard.amountNegative",
        });
      }
    }

    if (!formData.currency.trim()) {
      newErrors.currency = intl.formatMessage({
        id: "admin.dashboard.currencyRequired",
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm() && priceToEdit) {
      const amount = Math.round(parseFloat(formData.amount) * 100); // Convert to cents
      const updatedPrice: Price = {
        ...priceToEdit,
        amount,
        currency: formData.currency,
        numberOfClasses: parseInt(formData.numberOfClasses),
        specialType:
          formData.priceType === "special" ? formData.specialType : null,
        classId:
          formData.priceType === "special" && formData.selectedClass
            ? formData.selectedClass.id
            : null, // Explicitly set to null for general prices
        isAdminOnly: formData.isAdminOnly,
      };
      onSave(updatedPrice);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      priceType: "general",
      numberOfClasses: "",
      specialType: "",
      amount: "",
      currency: "chf",
      selectedClass: null,
      isAdminOnly: false,
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage id="admin.dashboard.editPrice" />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              <FormattedMessage id="admin.dashboard.priceType" />
            </FormLabel>
            <RadioGroup
              value={formData.priceType}
              onChange={(e) => handlePriceTypeChange(e.target.value)}
            >
              <FormControlLabel
                value="general"
                control={<Radio />}
                label={<FormattedMessage id="admin.dashboard.generalPrice" />}
              />
              <FormControlLabel
                value="special"
                control={<Radio />}
                label={<FormattedMessage id="admin.dashboard.specialPrice" />}
              />
            </RadioGroup>
          </FormControl>

          {formData.priceType === "special" && (
            <>
              <TextField
                fullWidth
                label={intl.formatMessage({
                  id: "admin.dashboard.specialType",
                })}
                value={formData.specialType}
                onChange={(e) =>
                  handleInputChange("specialType", e.target.value)
                }
                error={!!errors.specialType}
                helperText={errors.specialType}
                placeholder={intl.formatMessage({
                  id: "admin.dashboard.specialTypePlaceholder",
                })}
              />

              <Autocomplete
                options={classes.filter((cls) => cls.isSpecial)}
                getOptionLabel={(option) =>
                  option.title.en || option.title.uk || option.title.fr
                }
                value={formData.selectedClass}
                onChange={(_, value) => {
                  setFormData((prev) => ({ ...prev, selectedClass: value }));
                  if (errors.selectedClass) {
                    setErrors((prev) => ({ ...prev, selectedClass: "" }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={intl.formatMessage({
                      id: "admin.dashboard.selectSpecialClass",
                    })}
                    error={!!errors.selectedClass}
                    helperText={errors.selectedClass}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </>
          )}

          <TextField
            fullWidth
            label={intl.formatMessage({
              id: "admin.dashboard.numberOfClasses",
            })}
            value={formData.numberOfClasses}
            onChange={(e) =>
              handleInputChange("numberOfClasses", e.target.value)
            }
            error={!!errors.numberOfClasses}
            helperText={errors.numberOfClasses}
            type="number"
            inputProps={{ min: "1", step: "1" }}
          />

          <TextField
            fullWidth
            label={intl.formatMessage({ id: "admin.dashboard.amount" })}
            value={formData.amount}
            onChange={(e) => handleInputChange("amount", e.target.value)}
            error={!!errors.amount}
            helperText={errors.amount}
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            disabled={formData.isAdminOnly}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">Fr</InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isAdminOnly}
                onChange={(e) =>
                  handleInputChange("isAdminOnly", e.target.checked)
                }
              />
            }
            label={intl.formatMessage({ id: "admin.dashboard.adminOnlyPrice" })}
          />

          <FormControl fullWidth error={!!errors.currency}>
            <InputLabel>
              <FormattedMessage id="admin.dashboard.currency" />
            </InputLabel>
            <Select
              value={formData.currency}
              label={intl.formatMessage({ id: "admin.dashboard.currency" })}
              onChange={(e) => handleInputChange("currency", e.target.value)}
            >
              {currencies.map((currency) => (
                <MenuItem key={currency.value} value={currency.value}>
                  {currency.label}
                </MenuItem>
              ))}
            </Select>
            {errors.currency && (
              <Box sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5 }}>
                {errors.currency}
              </Box>
            )}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          <FormattedMessage id="admin.dashboard.cancel" />
        </Button>
        <Button onClick={handleSave} variant="contained">
          <FormattedMessage id="admin.dashboard.save" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPriceDialog;
